<template>
  <DialogRoot
    :open="isOpen || isStorybook"
    :modal="!isStorybook"
    @update:open="closeDialog"
  >
    <DialogPortal :disabled="isStorybook">
      <div :class="[DIALOG_CLASS, $style.root]">
        <DialogOverlay
          v-if="!isStorybook"
          class="fixed inset-0 bg-gray-100/50 backdrop-blur-sm dark:bg-gray-800/20"
          :class="$style.overlay"
        />
        <DialogContent
          :ref="content.forwardRef"
          :aria-describedby="undefined"
          :class="[
            !isStorybook && $style.content,
            `flex flex-col`,
            isStorybook
              ? `relative mx-auto my-8 rounded-lg`
              : [
                  `fixed left-1/2`,
                  isMobile ? `bottom-0 rounded-t-lg` : `top-1/2 rounded-lg`,
                ],

            `bg-background text-foreground shadow-2xl`,
            !isStorybook && isMobile ? `border-t` : `border`,
            'border-gray-300 dark:border-border',

            slots.actions && `scroll-pb-20`,

            `w-full overflow-y-auto`,
            fullHeight && `h-full`,
            'transition-all duration-200',
            widthClass,
          ]"
        >
          <slot v-if="$slots.header" name="header"></slot>
          <div v-else-if="title !== null" class="p-4">
            <div
              v-if="slots.breadcrumb"
              class="flex min-w-0 flex-wrap items-center gap-1.5"
            >
              <slot name="breadcrumb"></slot>
              <ChevronRightIcon
                class="size-4 shrink-0 -rotate-90 text-muted-foreground"
              />
              <DialogTitle class="min-w-0 break-words text-base font-bold">
                {{ title }}
              </DialogTitle>
            </div>
            <DialogTitle v-else class="break-words text-base font-bold">
              {{ title }}
            </DialogTitle>
          </div>

          <Callout.Container v-if="slots.callouts" class="mb-4">
            <slot name="callouts"></slot>
          </Callout.Container>

          <div
            :class="[
              `grow px-4`,
              !($slots.header || title !== null) && `pt-4`,
              !slots.actions && `pb-4`,
              containerClass,
            ]"
          >
            <slot></slot>
          </div>

          <div
            v-if="slots.actions"
            :class="[
              $style.actions,
              `sticky bottom-0 z-10 flex justify-between gap-4 border-t bg-white p-4 dark:bg-background`,
              isMobile && `flex-col`,
              showActionsBorder
                ? `border-border bg-white dark:bg-background`
                : `border-transparent`,
            ]"
          >
            <slot name="actions"></slot>
          </div>
        </DialogContent>
      </div>
    </DialogPortal>
  </DialogRoot>
</template>

<script lang="ts">
/** @deprecated */
export const DIALOG_CLASS = "buttondown-dialog-root";
</script>

<script setup lang="ts">
import {
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  useForwardExpose,
} from "reka-ui";
import { computed, onWatcherCleanup, ref, watch } from "vue";

import Callout from "@/design_system/Callout";
import ChevronRightIcon from "@/icons/heroicons/chevron-down-micro.svg";
import { useMediaQuery } from "@/lib/composables/media-query";
import { useKeybinds } from "@/lib/hotkeys";

const emit = defineEmits<{
  close: [];
}>();

const slots = defineSlots<{
  default(props: {}): unknown;
  header?(props: {}): unknown;
  breadcrumb?(props: {}): unknown;
  actions?(props: {}): unknown;
  callouts?(props: {}): unknown;
}>();

const props = withDefaults(
  defineProps<{
    title: string | null;
    variant?: "wide" | "very-wide" | "super-wide" | "narrow";
    containerClass?: string;
    fullHeight?: boolean;
  }>(),
  {
    variant: "wide",
  }
);

const content = useForwardExpose();

const showActionsBorder = ref(false);

const isMobile = useMediaQuery("(width < 576px)");
const isOpen = ref(true);

// This is already enforced with the types
// eslint-disable-next-line vue/return-in-computed-property
const widthClass = computed((): string => {
  switch (props.variant) {
    case "wide": {
      return `max-w-xl`;
    }
    case "narrow": {
      return `md:max-w-md`;
    }
    case "very-wide": {
      return `max-w-3xl md:min-w-[20rem]`;
    }
    case "super-wide": {
      return `max-w-7xl md:min-w-[20rem]`;
    }
  }
});

const closeDialog = () => {
  isOpen.value = false;
  setTimeout(() => emit("close"), 250);
};

watch(
  [content.currentElement, () => !!slots.actions],
  ([contentEl, hasActions]) => {
    if (!contentEl || !hasActions) {
      return;
    }

    const update = () => {
      const clientHeight = contentEl.clientHeight;
      const scrollHeight = contentEl.scrollHeight;
      const scrollTop = contentEl.scrollTop;

      const isScrollable = scrollHeight > clientHeight;
      const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 2;

      showActionsBorder.value = isScrollable && !isAtBottom;
    };

    const observer = new ResizeObserver(update);

    observer.observe(contentEl);
    contentEl.addEventListener("scroll", update, { passive: true });

    onWatcherCleanup(() => {
      observer.disconnect();
      contentEl.removeEventListener("scroll", update);
    });
  },
  { immediate: true }
);

const isStorybook = computed(() =>
  window.location.pathname.includes("vue-storybook")
);

useKeybinds(() => {
  if (!isOpen.value) {
    return;
  }

  return {
    Escape: {
      name: `Close dialog`,
      group: `Dialog`,
      // Handled by Radix Vue
      run: null,
    },
  };
});
</script>

<style module>
.root {
  /**
   * `position` must be non-static for `z-index` to take effect. Without it the
   * dialog stacks at the root level with no effective z-index and can be
   * painted over by any positioned, z-indexed element (e.g. the editor's
   * resize handle). `relative` doesn't affect the fixed overlay/content.
   */
  position: relative;
  z-index: var(--z-dialog);
}

.overlay {
  @media (prefers-reduced-motion: no-preference) {
    &[data-state="open"] :global {
      animation: fade-in 250ms var(--motion-timing-ease);
    }
    &[data-state="closed"] :global {
      animation: fade-out 250ms var(--motion-timing-ease);
    }
  }
}

.content {
  /**
   * Max height is 820px, or screen height - 48px * 2 (top and bottom),
   * whichever is the lowest, a little bit awkward to describe in Tailwind.
   */
  max-height: min(820px, calc(100% - (48px * 2)));
  translate: -50% -50%;

  @media (prefers-reduced-motion: no-preference) {
    &[data-state="open"] :global {
      animation: pop-in 250ms var(--motion-timing-ease);
    }
    &[data-state="closed"] :global {
      animation: pop-out 250ms var(--motion-timing-ease);
    }
  }

  /**
   * We're a bit cramped for space wrt mobile, so let's bump the max-height
   * a little bit (65px = header height + 12px)
   */
  @media (width < 576px) {
    max-height: min(820px, calc(100% - 65px));
    translate: -50% 0%;

    @media (prefers-reduced-motion: no-preference) {
      &[data-state="open"] {
        animation: content-slide-in 250ms var(--motion-timing-ease);
      }
      &[data-state="closed"] {
        animation: content-slide-out 250ms var(--motion-timing-ease);
      }
    }
  }
}

/**
 * Awkward to use Tailwind for a one-off, this is just `transition-colors` but
 * it only applies to `border-color`. I've noticed that if `background-color`
 * was set all the time it would cut off the outlines of inputs.
 */
.actions {
  transition: border-color 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes content-slide-in {
  from {
    opacity: 0;
    translate: -50% 16px;
  }
  to {
    opacity: 1;
    translate: -50% 0;
  }
}

@keyframes content-slide-out {
  from {
    opacity: 1;
    translate: -50% 0;
  }
  to {
    opacity: 0;
    translate: -50% 16px;
  }
}
</style>
